import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { login, fetchMe, ApiError } from '../api/client';
import { useAuthStore } from '../auth/store';
import { Button, FormField, Input } from '../components/ui';

// Recognise the server's "2FA required" signal. The API returns 401 with
// an error code/message indicating the account has TOTP enabled and the
// submitted credentials were otherwise valid; we then reveal the TOTP field
// instead of showing a generic failure.
function isTwoFactorRequired(err: ApiError): boolean {
  const body = err.body;
  if (body && typeof body === 'object') {
    const code = (body as { code?: unknown }).code;
    if (typeof code === 'string' && /totp|2fa|two.?factor|mfa/i.test(code)) return true;
    const error = (body as { error?: unknown }).error;
    if (typeof error === 'string' && /totp|2fa|two.?factor|mfa|authenticator/i.test(error)) {
      return true;
    }
  }
  return /totp|2fa|two.?factor|mfa|authenticator/i.test(err.message);
}

export function LoginPage(): JSX.Element {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  const [needTotp, setNeedTotp] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const setMe = useAuthStore((s) => s.setMe);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login({
        email,
        password,
        ...(needTotp && totp ? { totp_code: totp.trim() } : {}),
      });
      const { user, ...rest } = await fetchMe();
      // Post-login /api/me must include a user — if it doesn't, the
      // server is in a weird state; treat it as a failed login.
      if (!user) throw new ApiError(401, null, 'Invalid email or password');
      setMe({ user, ...rest });
      navigate('/app', { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        // Rate limiting / lockout — surface the throttle clearly.
        if (err.status === 429) {
          setError('Too many attempts. Please wait a moment and try again.');
        } else if (isTwoFactorRequired(err)) {
          setNeedTotp(true);
          setError(
            needTotp
              ? 'That authenticator code was not accepted. Try again.'
              : 'Enter the 6-digit code from your authenticator app.',
          );
        } else if (err.status === 401) {
          setError('Invalid email or password.');
        } else {
          setError('Something went wrong. Please try again.');
        }
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-page">
      <form className="login-form" onSubmit={onSubmit} noValidate>
        <div className="login-brand">
          <img src="/logo-mark.png" alt="Darkspire Risk" className="login-logo" />
          <h1>
            Darkspire <span className="brand-risk">Risk</span>
          </h1>
        </div>
        <p className="login-subtitle">Operations Console — sign in to continue</p>

        <FormField label="Email" required>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
            autoFocus
          />
        </FormField>

        <FormField label="Password" required>
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="current-password"
          />
        </FormField>

        {needTotp && (
          <FormField label="Authenticator code">
            <Input
              type="text"
              value={totp}
              onChange={(e) => setTotp(e.target.value.replace(/\D/g, '').slice(0, 8))}
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="123456"
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
            />
          </FormField>
        )}

        {error && (
          <p className="login-error" role="alert">
            {error}
          </p>
        )}

        <Button type="submit" variant="primary" block disabled={submitting}>
          {submitting ? 'Signing in…' : 'Sign in'}
        </Button>
      </form>
    </div>
  );
}
